import { useState } from 'react';
import back4app2Client from '../back4app2';
import { ReactComponent as CheckIcon } from '../assets/images/check-icon.svg';
import Button from './Button';

const MIN_MESSAGE = 30;
const MAX_MESSAGE = 2000;

/**
 * The customer's one review request.
 *
 * The count is stated BEFORE they submit, not after: discovering the limit by
 * hitting it is how a blocked customer ends up in the support queue twice —
 * once for the block and once because the product surprised them.
 */
const AppealBlockModal = ({
  appId,
  contactEmail,
  onClose,
  onSubmitted,
}: {
  appId: string;
  contactEmail?: string;
  onClose: () => void;
  onSubmitted: () => void;
}) => {
  const [message, setMessage] = useState('');
  const [attested, setAttested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const tooShort = message.trim().length < MIN_MESSAGE;
  const canSubmit = !tooShort && attested && !submitting;

  const submit = async () => {
    setSubmitting(true);
    setError(undefined);

    try {
      await back4app2Client.submitAppBlockAppeal(
        appId,
        message.trim(),
        attested,
        contactEmail || undefined
      );
      onSubmitted();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'we could not send your request'
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-950/60 backdrop-blur-md p-4">
      <div className="w-full max-w-lg rounded-lg bg-dark-grey p-6 font-inter">
        <h2 className="font-sora text-lg font-bold mb-2">Request a review</h2>

        <p className="text-sm text-white/70 mb-4">
          Tell us what this app does and what the flagged code is for.
        </p>

        <textarea
          className="w-full h-32 rounded bg-black/30 p-3 text-sm outline-none"
          placeholder="What does this app do?"
          maxLength={MAX_MESSAGE}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
        />
        <div className="text-xs text-white/50 mb-4">
          {message.trim().length}/{MAX_MESSAGE}
          {tooShort ? ` — at least ${MIN_MESSAGE} characters` : ''}
        </div>

        {/* No field: we already know where to reply. Asking someone to
            confirm their own account's address is a question with one right
            answer, which is not a question. Stated instead, so the reply does
            not arrive as a surprise. */}
        {contactEmail ? (
          <p className="text-xs text-white/50 mb-4">
            We&apos;ll reply to {contactEmail}.
          </p>
        ) : null}

        {/* The attestation is the one thing in this form that carries weight -
            it is what makes a bad-faith request a written violation on its own
            - so it gets a control that looks deliberate rather than a bare
            browser checkbox, which on a dark background renders as a white
            square with no border in its unchecked state.

            The real input stays in the DOM, visually hidden, so the label,
            the keyboard and screen readers keep working; the box beside it
            only draws the state. */}
        <label
          className={`flex items-start gap-3 rounded border p-3 mb-4 cursor-pointer transition-colors ${
            attested
              ? 'border-cta-green/60 bg-cta-green/5'
              : 'border-light-blue/20 hover:border-light-blue/40'
          }`}
        >
          <span
            className={`h-4 w-4 mt-0.5 flex-none rounded-sm border flex items-center justify-center transition-colors ${
              attested
                ? 'bg-cta-green border-cta-green'
                : 'border-light-blue/40 bg-transparent'
            }`}
          >
            {attested ? <CheckIcon className="w-3 h-3 text-white" /> : null}
          </span>
          <input
            type="checkbox"
            className="sr-only"
            checked={attested}
            onChange={(event) => setAttested(event.target.checked)}
          />
          {/* Points at the policy instead of listing what we look for. The
              list named the categories we detect, which is the half of the
              policy worth having if you are working around it — the same
              reason it came out of the block message. Naming the document is
              also the stronger attestation: the policy is authoritative and
              can be updated, a sentence hand-written here cannot. */}
          <span className="text-sm leading-140 text-white">
            I confirm this app complies with Back4app&apos;s Acceptable Use
            Policy.
          </span>
        </label>

        {/* Said before the button, not after they press it. */}
        <p className="text-xs text-white/50 mb-4">
          You can send one review request for this app. Once you submit, you
          won&apos;t be able to send another.
        </p>

        {error ? (
          <p className="text-sm text-error-red mb-4">{error}</p>
        ) : null}

        <div className="flex gap-3 justify-end">
          <button
            type="button"
            className="text-sm text-white/70 px-4"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <Button type="primary" isDisabled={!canSubmit} onClick={submit}>
            {submitting ? 'Sending…' : 'Send request'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AppealBlockModal;
